import { useCallback, useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { FileText, Upload, Download, Eye, RotateCw } from 'lucide-react';

const STATUS_LABEL = {
  pending: 'Pendiente',
  received: 'Recibida',
  delivered_to_buyer: 'Entregada al comprador',
  error: 'Error',
};
const STATUS_COLOR = {
  pending: 'bg-slate-100 text-slate-700 border-slate-300',
  received: 'bg-amber-100 text-amber-800 border-amber-300',
  delivered_to_buyer: 'bg-emerald-100 text-emerald-800 border-emerald-300',
  error: 'bg-rose-100 text-rose-800 border-rose-300',
};

/**
 * Sección "Factura electrónica" reutilizada por SellerOrders + BuyerOrders.
 * - role="seller": permite subir/reemplazar/notify-retry.
 * - role="buyer": solo consulta y descarga.
 */
export function InvoiceSection({ orderId, role, sellerId, apiBase }) {
  const [invoice, setInvoice] = useState(null);
  const [status, setStatus] = useState('pending');
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  // Solo seller
  const [pdf, setPdf] = useState(null);
  const [xml, setXml] = useState(null);
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [cufe, setCufe] = useState('');
  const [invoiceDate, setInvoiceDate] = useState('');
  const [replaceReason, setReplaceReason] = useState('');

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      if (role === 'seller') {
        const { data } = await api.get(`/sellers/me/orders/${orderId}/invoice`);
        setInvoice(data.invoice);
        setStatus(data.status || 'pending');
      } else {
        const { data } = await api.get(`/orders/${orderId}/invoices`);
        const items = data.items || [];
        const own = sellerId ? items.find((i) => i.seller_id === sellerId) : items[0];
        setInvoice(own || null);
        setStatus(own?.status || 'pending');
      }
    } catch (_) {
      setInvoice(null);
      setStatus('pending');
    } finally { setLoading(false); }
  }, [orderId, role, sellerId]);

  useEffect(() => { refresh(); }, [refresh]);

  const doUpload = async () => {
    if (!pdf) { toast.error('Selecciona el PDF de la factura'); return; }
    const isReplacing = !!invoice && (status === 'delivered_to_buyer');
    if (isReplacing && !replaceReason.trim()) {
      toast.error('Explica el motivo del reemplazo');
      return;
    }
    setUploading(true);
    const fd = new FormData();
    fd.append('pdf', pdf);
    if (xml) fd.append('xml', xml);
    if (invoiceNumber.trim()) fd.append('invoice_number', invoiceNumber.trim());
    if (cufe.trim()) fd.append('cufe', cufe.trim());
    if (invoiceDate) fd.append('invoice_date', invoiceDate);
    if (replaceReason.trim()) fd.append('replace_reason', replaceReason.trim());
    try {
      const { data } = await api.post(
        `/sellers/me/orders/${orderId}/invoice`, fd,
        { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(isReplacing
        ? 'Factura reemplazada y comprador notificado'
        : 'Factura cargada y comprador notificado');
      setPdf(null); setXml(null); setReplaceReason('');
      if (data.auto_extracted?.invoice_number && !invoiceNumber) {
        setInvoiceNumber(data.auto_extracted.invoice_number);
      }
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo cargar la factura');
    } finally { setUploading(false); }
  };

  const doDownload = async (kind = 'pdf') => {
    if (!invoice?.invoice_id) return;
    try {
      const r = await api.get(`/invoices/${invoice.invoice_id}/download`,
        { params: { kind }, responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `factura_${invoice.invoice_number || invoice.invoice_id}.${kind}`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('No se pudo descargar');
    }
  };

  const doView = async () => {
    if (!invoice?.invoice_id) return;
    try {
      const r = await api.get(`/invoices/${invoice.invoice_id}/download`,
        { params: { kind: 'pdf' }, responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      toast.error('No se pudo abrir');
    }
  };

  const doNotifyRetry = async () => {
    try {
      await api.post(`/sellers/me/orders/${orderId}/invoice/notify-retry`, {});
      toast.success('Notificación al comprador reintentada');
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo reintentar');
    }
  };

  if (loading) return (
    <div className="border-t border-slate-200 bg-slate-50 px-4 py-3 text-xs text-slate-500">
      Cargando factura...
    </div>
  );

  const badge = (
    <Badge className={`rounded-none border ${STATUS_COLOR[status] || STATUS_COLOR.pending}`}>
      {STATUS_LABEL[status] || status}
    </Badge>
  );

  return (
    <div className="border-t border-slate-200 bg-slate-50 px-4 py-3" data-testid={`invoice-section-${orderId}`}>
      <div className="flex items-center gap-2 flex-wrap">
        <FileText className="h-4 w-4 text-slate-600" />
        <span className="text-sm font-bold uppercase tracking-wider text-slate-700">Factura electrónica</span>
        {badge}
        {invoice?.invoice_number && (
          <span className="text-xs font-mono text-slate-600">N° {invoice.invoice_number}</span>
        )}
        {invoice?.version > 1 && (
          <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
            v{invoice.version}
          </span>
        )}
      </div>

      {/* Buyer view */}
      {role === 'buyer' && (
        <div className="mt-3">
          {invoice ? (
            <div className="flex items-center gap-2 flex-wrap">
              <Button
                data-testid={`invoice-view-${orderId}`}
                onClick={doView} size="sm"
                className="rounded-none bg-slate-900 hover:bg-slate-800 h-9">
                <Eye className="h-4 w-4 mr-1" /> Ver factura
              </Button>
              <Button
                data-testid={`invoice-download-${orderId}`}
                onClick={() => doDownload('pdf')} size="sm" variant="outline"
                className="rounded-none h-9">
                <Download className="h-4 w-4 mr-1" /> Descargar
              </Button>
              {invoice.has_xml && (
                <Button
                  data-testid={`invoice-download-xml-${orderId}`}
                  onClick={() => doDownload('xml')} size="sm" variant="outline"
                  className="rounded-none h-9">
                  <Download className="h-4 w-4 mr-1" /> XML
                </Button>
              )}
            </div>
          ) : (
            <div className="text-xs text-slate-500">
              El vendedor aún no ha cargado la factura. Te avisaremos cuando esté disponible.
            </div>
          )}
        </div>
      )}

      {/* Seller view */}
      {role === 'seller' && (
        <div className="mt-3 space-y-2">
          {invoice && (
            <div className="flex items-center gap-2 flex-wrap">
              <Button data-testid={`invoice-view-${orderId}`} onClick={doView} size="sm" variant="outline"
                className="rounded-none h-9"><Eye className="h-4 w-4 mr-1" /> Ver PDF</Button>
              {status !== 'delivered_to_buyer' ? null : (
                <Button data-testid={`invoice-retry-${orderId}`} onClick={doNotifyRetry} size="sm" variant="outline"
                  className="rounded-none h-9"><RotateCw className="h-4 w-4 mr-1" /> Reenviar aviso</Button>
              )}
            </div>
          )}
          <details className="border border-slate-200 bg-white">
            <summary className="cursor-pointer px-3 py-2 text-xs font-bold uppercase tracking-wider text-slate-700">
              {invoice ? 'Reemplazar factura' : 'Adjuntar factura'}
            </summary>
            <div className="p-3 grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="md:col-span-2">
                <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">PDF de la factura *</label>
                <Input data-testid={`invoice-pdf-${orderId}`} type="file" accept="application/pdf"
                  onChange={(e) => setPdf(e.target.files?.[0] || null)}
                  className="rounded-none mt-1" />
              </div>
              <div className="md:col-span-2">
                <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">XML (opcional)</label>
                <Input data-testid={`invoice-xml-${orderId}`} type="file" accept="application/xml,text/xml"
                  onChange={(e) => setXml(e.target.files?.[0] || null)}
                  className="rounded-none mt-1" />
              </div>
              <div>
                <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">
                  N° factura (si no se extrae del PDF)
                </label>
                <Input data-testid={`invoice-number-${orderId}`} value={invoiceNumber}
                  onChange={(e) => setInvoiceNumber(e.target.value)}
                  className="rounded-none mt-1" placeholder="FE-000123" />
              </div>
              <div>
                <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">Fecha (opcional)</label>
                <Input data-testid={`invoice-date-${orderId}`} type="date" value={invoiceDate}
                  onChange={(e) => setInvoiceDate(e.target.value)}
                  className="rounded-none mt-1" />
              </div>
              <div className="md:col-span-2">
                <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">
                  CUFE (opcional — 96 caracteres hex)
                </label>
                <Input data-testid={`invoice-cufe-${orderId}`} value={cufe}
                  onChange={(e) => setCufe(e.target.value)}
                  className="rounded-none mt-1 font-mono text-xs" maxLength={96} />
              </div>
              {invoice && status === 'delivered_to_buyer' && (
                <div className="md:col-span-2">
                  <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">
                    Motivo del reemplazo (obligatorio) *
                  </label>
                  <Input data-testid={`invoice-reason-${orderId}`} value={replaceReason}
                    onChange={(e) => setReplaceReason(e.target.value)}
                    placeholder="Ej. Corrección IVA / número"
                    className="rounded-none mt-1" />
                </div>
              )}
              <div className="md:col-span-2">
                <Button
                  data-testid={`invoice-upload-${orderId}`}
                  onClick={doUpload} disabled={uploading || !pdf}
                  className="rounded-none bg-slate-900 hover:bg-slate-800 h-10 font-bold">
                  <Upload className="h-4 w-4 mr-2" />
                  {uploading ? 'Cargando...' : (invoice ? 'Reemplazar factura' : 'Adjuntar factura')}
                </Button>
              </div>
            </div>
          </details>
        </div>
      )}
    </div>
  );
}
